import React from "react";
import { Plus, Minus, Type, List, AlignLeft } from "lucide-react";
import Odontogram from "../../../../../components/odontogram/Odontogram";
import { BaseFieldPreviewProps } from "./previewUtils";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { FormField } from "../../../types/formTypes";
import { createNewOdontogramField } from "../../../utils/fieldUtils";
import { ToothData } from "@/dentconnect/src/pages/job-request/types";
import TextFieldPreview from "./TextFieldPreview";
import TextareaFieldPreview from "./TextareaFieldPreview";

const OdontogramFieldPreview: React.FC<BaseFieldPreviewProps> = ({
	field,
	renderDescription,
	onUpdateOptions,
}) => {
	// Function to add a new field
	const addOdontogramField = (type: "select" | "text" | "textarea" = "select") => {
		if (!field.odontogramFields) {
			field.odontogramFields = [];
		}

		const newOdontogramField = createNewOdontogramField(type);
		const updatedOdontogramFields = [
			...field.odontogramFields,
			newOdontogramField,
		];

		// Create a custom event to notify about the odontogram fields update
		const customEvent = new CustomEvent("odontogramFieldsUpdate", {
			detail: {
				fieldId: field.id,
				odontogramFields: updatedOdontogramFields,
			},
			bubbles: true,
		});

		// Dispatch the event
		document.dispatchEvent(customEvent);

		// Update the field directly as a temporary solution
		field.odontogramFields = updatedOdontogramFields;
	};

	// Function to remove a dropdown field
	const removeOdontogramField = (index: number) => {
		if (!field.odontogramFields || field.odontogramFields.length === 0)
			return;

		const updatedOdontogramFields = [...field.odontogramFields];
		updatedOdontogramFields.splice(index, 1);

		// Create a custom event to notify about the odontogram fields update
		const customEvent = new CustomEvent("odontogramFieldsUpdate", {
			detail: {
				fieldId: field.id,
				odontogramFields: updatedOdontogramFields,
			},
			bubbles: true,
		});

		// Dispatch the event
		document.dispatchEvent(customEvent);

		// Update the field directly as a temporary solution
		field.odontogramFields = updatedOdontogramFields;
	};

	// Function to update dropdown field label
	const updateOdontogramFieldLabel = (index: number, newLabel: string) => {
		if (!field.odontogramFields) return;

		const updatedOdontogramFields = [...field.odontogramFields];
		updatedOdontogramFields[index] = {
			...updatedOdontogramFields[index],
			label: newLabel,
		};

		// Create a custom event to notify about the odontogram fields update
		const customEvent = new CustomEvent("odontogramFieldsUpdate", {
			detail: {
				fieldId: field.id,
				odontogramFields: updatedOdontogramFields,
			},
			bubbles: true,
		});

		// Dispatch the event
		document.dispatchEvent(customEvent);

		// Update the field directly as a temporary solution
		field.odontogramFields = updatedOdontogramFields;
	};

	// Function to add option to a dropdown field
	const addOptionToOdontogramField = (fieldIndex: number) => {
		if (!field.odontogramFields) return;

		const odontogramField = field.odontogramFields[fieldIndex];
		if (!odontogramField || !odontogramField.options) return;

		const newOptions = [
			...odontogramField.options,
			`Option ${odontogramField.options.length + 1}`,
		];

		const updatedOdontogramFields = [...field.odontogramFields];
		updatedOdontogramFields[fieldIndex] = {
			...odontogramField,
			options: newOptions,
		};

		// Create a custom event to notify about the odontogram fields update
		const customEvent = new CustomEvent("odontogramFieldsUpdate", {
			detail: {
				fieldId: field.id,
				odontogramFields: updatedOdontogramFields,
			},
			bubbles: true,
		});

		// Dispatch the event
		document.dispatchEvent(customEvent);

		// Update the field directly as a temporary solution
		field.odontogramFields = updatedOdontogramFields;
	};

	// Function to remove option from a dropdown field
	const removeOptionFromOdontogramField = (
		fieldIndex: number,
		optionIndex: number
	) => {
		if (!field.odontogramFields) return;

		const odontogramField = field.odontogramFields[fieldIndex];
		if (
			!odontogramField ||
			!odontogramField.options ||
			odontogramField.options.length <= 2
		)
			return;

		const updatedOptions = [...odontogramField.options];
		updatedOptions.splice(optionIndex, 1);

		const updatedOdontogramFields = [...field.odontogramFields];
		updatedOdontogramFields[fieldIndex] = {
			...odontogramField,
			options: updatedOptions,
		};

		// Create a custom event to notify about the odontogram fields update
		const customEvent = new CustomEvent("odontogramFieldsUpdate", {
			detail: {
				fieldId: field.id,
				odontogramFields: updatedOdontogramFields,
			},
			bubbles: true,
		});

		// Dispatch the event
		document.dispatchEvent(customEvent);

		// Update the field directly as a temporary solution
		field.odontogramFields = updatedOdontogramFields;
	};

	// Function to update option in a dropdown field
	const updateOptionLabel = (
		fieldIndex: number,
		optionIndex: number,
		newLabel: string
	) => {
		if (!field.odontogramFields) return;

		const odontogramField = field.odontogramFields[fieldIndex];
		if (!odontogramField || !odontogramField.options) return;

		const updatedOptions = [...odontogramField.options];
		updatedOptions[optionIndex] = newLabel;

		const updatedOdontogramFields = [...field.odontogramFields];
		updatedOdontogramFields[fieldIndex] = {
			...odontogramField,
			options: updatedOptions,
		};

		// Create a custom event to notify about the odontogram fields update
		const customEvent = new CustomEvent("odontogramFieldsUpdate", {
			detail: {
				fieldId: field.id,
				odontogramFields: updatedOdontogramFields,
			},
			bubbles: true,
		});

		// Dispatch the event
		document.dispatchEvent(customEvent);

		// Update the field directly as a temporary solution
		field.odontogramFields = updatedOdontogramFields;
	};

	// Render a mock field preview based on field type
	const renderFieldPreview = (odontogramField: FormField) => {
		// Create a simple rendering function for descriptions
		const mockRenderDescription = () => null;
		
		switch (odontogramField.type) {
			case "text":
				return (
						<TextFieldPreview 
							field={odontogramField} 
							renderDescription={mockRenderDescription}
						/>
				);
			case "textarea":
				return (
						<TextareaFieldPreview 
							field={odontogramField} 
							renderDescription={mockRenderDescription}
						/>
				);
			default:
				return null;
		}
	};

	return (
		<div>
			{renderDescription()}
			<div className="border border-input rounded-md overflow-hidden mb-4">
				<Odontogram
					selectedTeeth={
						field.defaultValue
							? (field.defaultValue as ToothData[])
							: []
					}
					disabled={true}
					odontogramFields={field.odontogramFields}
				/>
			</div>

			{/* Fields Configuration */}
			<div className="mt-4">
				<h3 className="text-sm font-medium mb-2">
					Zahnoptionen konfigurieren
				</h3>
				<p className="text-xs text-gray-500 mb-2">
					Konfigurieren Sie zusätzliche Informationen, die für jeden
					ausgewählten Zahn erfasst werden sollen.
				</p>

				{/* List of fields */}
				{field.odontogramFields && field.odontogramFields.length > 0 ? (
					<div className="space-y-4 mb-4">
						{field.odontogramFields.map(
							(
								odontogramField: FormField,
								fieldIndex: number
							) => (
								<div
									key={odontogramField.id}
									className="border border-gray-200 rounded-md p-3"
								>
									{/* Field header */}
									<div className="flex items-center justify-between mb-2">
										<div className="flex-1">
											<Input
												value={odontogramField.label}
												onChange={(e) =>
													updateOdontogramFieldLabel(
														fieldIndex,
														e.target.value
													)
												}
												placeholder="Feldbezeichnung"
												className="mb-2"
											/>
										</div>
										<Button
											variant="ghost"
											size="sm"
											onClick={() =>
												removeOdontogramField(
													fieldIndex
												)
											}
											className="ml-2 text-red-500 hover:text-red-700"
										>
											<Minus className="h-4 w-4" />
										</Button>
									</div>

									{/* Field-specific UI */}
									{odontogramField.type === "select" && (
										<div className="space-y-2 pl-2">
											{odontogramField.options &&
												odontogramField.options.map(
													(option, optionIndex) => (
														<div
															key={optionIndex}
															className="flex items-center gap-2"
														>
															<div className="text-xs text-gray-500 w-4">
																{optionIndex + 1}.
															</div>
															<Input
																value={option}
																onChange={(e) =>
																	updateOptionLabel(
																		fieldIndex,
																		optionIndex,
																		e.target
																			.value
																	)
																}
																className="flex-1 text-sm"
															/>
															<Button
																variant="ghost"
																size="sm"
																disabled={
																	(odontogramField
																		.options
																		?.length ||
																		0) <= 2
																}
																onClick={() =>
																	removeOptionFromOdontogramField(
																		fieldIndex,
																		optionIndex
																	)
																}
																className="p-0 h-8 w-8"
															>
																<Minus className="h-4 w-4" />
															</Button>
														</div>
													)
												)}

											{/* Add option button */}
											<div className="flex items-center gap-2 mt-2">
												<div className="text-xs text-gray-500 w-4">
													{(odontogramField.options
														?.length || 0) + 1}
													.
												</div>
												<Button
													variant="ghost"
													size="sm"
													onClick={() =>
														addOptionToOdontogramField(
															fieldIndex
														)
													}
													className="w-full flex items-center justify-start text-gray-500 h-8 text-sm"
												>
													<Plus className="h-3 w-3 mr-1" />
													Option hinzufügen
												</Button>
											</div>
										</div>
									)}

									{/* Text and textarea input fields */}
									{(odontogramField.type === "text" || odontogramField.type === "textarea") && 
                                        renderFieldPreview(odontogramField)}
								</div>
							)
						)}
					</div>
				) : (
					<div className="text-sm text-gray-500 italic mb-4">
						Keine zusätzlichen Felder konfiguriert. Bei der
						Zahnauswahl wird nur "Ausgewählt" oder "Nicht
						ausgewählt" angezeigt.
					</div>
				)}

				{/* Add field buttons */}
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => addOdontogramField("select")}
						className="flex-1 flex items-center justify-center"
					>
						<List className="h-4 w-4 mr-2" />
						Auswahlfeld
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => addOdontogramField("text")}
						className="flex-1 flex items-center justify-center"
					>
						<Type className="h-4 w-4 mr-2" />
						Kurze Antwort
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => addOdontogramField("textarea")}
						className="flex-1 flex items-center justify-center"
					>
						<AlignLeft className="h-4 w-4 mr-2" />
						Lange Antwort
					</Button>
				</div>
			</div>
		</div>
	);
};

export default OdontogramFieldPreview;
